import { useEffect, useState } from 'react';
import { Wrench } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { adminApi, type MaintenanceState } from '../utils/api';
import { Badge, Button, Card, Checkbox, Input, Textarea } from './ui';

/**
 * メンテナンスモードの切り替え（管理者ダッシュボード）。
 *
 * 統計の読み込み結果に関係なく常に描画する。バックエンドが不調なときこそメンテを
 * 出したいので、統計取得の失敗でこのパネルまで消えると使い物にならないため。
 */
export default function MaintenanceControl() {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [endsAtLocal, setEndsAtLocal] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getMaintenance()
      .then((state) => {
        if (cancelled) return;
        applyState(state);
      })
      .catch(() => {
        if (!cancelled) setError('メンテナンス設定の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyState = (state: MaintenanceState) => {
    setEnabled(state.enabled);
    setEndsAtLocal(state.ends_at ? toLocalInputValue(state.ends_at) : '');
    setMessage(state.message ?? '');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const state = await adminApi.updateMaintenance({
        enabled,
        ends_at: endsAtLocal ? new Date(endsAtLocal).toISOString() : null,
        message: message.trim() || null,
      });
      applyState(state);
      setSavedAt(new Date().toLocaleTimeString('ja-JP'));
      // 自分の画面のゲート判定も即座に追随させる
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    } catch (err) {
      console.error('Failed to update maintenance state:', err);
      setError('メンテナンス設定の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  /** 「いまから N 分後」を終了予定にセットする（メンテ開始時の入力を減らす） */
  const setRelativeEnd = (minutes: number) => {
    const at = new Date(Date.now() + minutes * 60_000);
    at.setSeconds(0, 0);
    setEndsAtLocal(toLocalInputValue(at.toISOString()));
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/20 rounded-lg">
            <Wrench className="w-6 h-6 text-primary" />
          </div>
          <div>
            <div className="font-bold">メンテナンスモード</div>
            <div className="text-sm text-text-sub">
              有効の間、機能ページはメンテ画面になります（管理者は通常どおり利用できます）
            </div>
          </div>
        </div>
        <Badge variant={enabled ? 'danger' : 'success'}>{enabled ? 'メンテ中' : '稼働中'}</Badge>
      </div>

      {loading ? (
        <p className="text-sm text-text-sub">読み込み中...</p>
      ) : (
        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              aria-label="メンテナンスモードを有効にする"
            />
            <span className="font-medium">メンテナンスモードを有効にする</span>
          </label>

          <div>
            <label htmlFor="maintenance-ends-at" className="block text-sm font-medium mb-1">
              終了予定時刻
              <span className="text-text-sub font-normal">（未入力なら画面に時刻を出しません）</span>
            </label>
            <Input
              id="maintenance-ends-at"
              type="datetime-local"
              value={endsAtLocal}
              onChange={(e) => setEndsAtLocal(e.target.value)}
              disabled={!enabled}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {[30, 60, 120, 180].map((m) => (
                <Button
                  key={m}
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!enabled}
                  onClick={() => setRelativeEnd(m)}
                >
                  {m < 60 ? `${m}分後` : `${m / 60}時間後`}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="maintenance-message" className="block text-sm font-medium mb-1">
              追記メッセージ
              <span className="text-text-sub font-normal">（任意）</span>
            </label>
            <Textarea
              id="maintenance-message"
              rows={2}
              maxLength={500}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!enabled}
              placeholder="例: 書き起こし精度の改善作業を行っています。"
            />
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : '保存して反映'}
            </Button>
            {savedAt && <span className="text-sm text-text-sub">{savedAt} に反映しました</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * ISO8601 を datetime-local 入力の値（ローカル時刻の YYYY-MM-DDTHH:mm）へ変換する。
 * toISOString() は UTC になってしまうため、ローカルの各成分から組み立てる。
 */
export function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
